import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { EMPTY, Observable, from, of, Subject, takeUntil } from 'rxjs';
import { concatMap, expand, map, reduce, tap, catchError } from 'rxjs/operators';

import { NotificationDetailsMainComponent } from '@shure/cloud/shared/notifications/feature-notification-details';
import {
	NotificationApiMessageCenterApiService,
	PostNotificationsRequest
} from '@shure/cloud/shared/notifications/notifications-data-access';
import { NotificationsStore } from '@shure/cloud/shared/notifications/notifications-store';
import {
	CHUNK_SIZE,
	NotificationFilterEnum,
	NotificationStoreData
} from '@shure/cloud/shared/notifications/shared/models/notification-models';
import { OrganizationsStoreService } from '@shure/cloud/shared/services/organizations-store-service';
import {
	ClickOutsideDirective,
	getConvertedDate,
	getConvertedDateTime,
	LoaderModule,
	updateDateFormat
} from '@shure/cloud/shared/ui/components';
import { CloudNavigationService } from '@shure/cloud/shared/utils/navigation';
import { PrismProgressIndicatorComponent, PrismSnackbarService } from '@shure/prism-angular-components';
import {
	PrismButtonComponent,
	PrismCardComponent,
	PrismDialogService,
	PrismIconButtonComponent,
	PrismListComponent,
	PrismListItemComponent
} from '@shure/prism-angular-components';
import { asLabel } from '@shure/shared/models';

@Component({
	selector: 'sh-notifications-popup',
	imports: [
		CommonModule,
		FormsModule,
		ReactiveFormsModule,
		TranslocoModule,
		PrismButtonComponent,
		PrismProgressIndicatorComponent,
		LoaderModule,
		PrismCardComponent,
		ClickOutsideDirective,
		PrismIconButtonComponent,
		PrismListComponent,
		PrismListItemComponent
	],
	templateUrl: './notifications-popup.component.html',
	styleUrls: ['./notifications-popup.component.scss']
})
export class NotificationsPopupComponent implements OnInit, OnDestroy {
	@Input() public application: string | undefined = '';
	@Output() public closeNotificationPopup = new EventEmitter();
	public isFirstClick = signal(true);
	public notifications$: Observable<NotificationStoreData[] | null> = EMPTY;
	public currentDate = new Date();
	public currentDateLabel = '';
	public isApiCallFailed = false;
	public readNotifications: Array<Pick<NotificationStoreData, 'id'> & Partial<NotificationStoreData>> = [];
	public successApiCallCount = 0;
	public readonly destroy$ = new Subject<void>();
	public notificationsData: NotificationStoreData[] = [];
	public nextPageToken?: string;
	public totalNotificationsCount = 0;
	public unReadNotificationsCalled = signal(false);
	public timeZone!: string | undefined;
	public displayDateFormat!: string;
	public isLoading = signal<boolean>(true);
	public unReadNotificationsDataLength = signal<number>(0);
	private readonly translationKeyMap = new Map<string, string>([
		['unable-to-mark-as-read', asLabel('cloud.shared.notification.unable-to-mark-as-read')],
		[
			'unable-to-mark-some-notifications-as-read',
			asLabel('cloud.shared.notification.unable-to-mark-some-notifications-as-read')
		]
	]);

	constructor(
		public notificationsStore: NotificationsStore,
		public notificationService: NotificationApiMessageCenterApiService,
		public dialogService: PrismDialogService,
		public router: Router,
		private translocoService: TranslocoService,
		private snackBarService: PrismSnackbarService,
		private organizationStore: OrganizationsStoreService,
		private navigationService: CloudNavigationService
	) {}

	/**
	 * Initializes the component and subscribe to the notifications store.
	 */
	public ngOnInit(): void {
		// Subscribe to the notifications data
		const accountInfo = this.organizationStore.accountInformation();
		const preferredDateFormat = accountInfo.preferences?.dateFormat || 'mm/dd/yyyy';
		const preferredTimeZone = accountInfo.preferences?.timeZone || 'UTC';

		this.timeZone = preferredTimeZone;
		this.displayDateFormat = updateDateFormat(preferredDateFormat);
		this.currentDateLabel = this.formatCurrentDate();
		this.notificationsStore.unReadNotifications$.pipe(takeUntil(this.destroy$)).subscribe((unreadNotifications) => {
			this.notificationsData = unreadNotifications;
			this.unReadNotificationsDataLength.set(this.notificationsData.length);
			this.notifications$ = of(
				unreadNotifications.map((item) => ({
					...item,
					body: item.body?.replace(/<[^>]*(>|$)| |‌|»|«|>/g, ' '),
					sentAt: item.sentAt ? getConvertedDateTime(this.timeZone, this.displayDateFormat, item.sentAt) : ''
				}))
			);
		});
		this.loadUnreadNotifications();
	}
	/**
	 * Handle table scroll event
	 * @param event represents Event
	 */
	public onNotificationListScroll(event: Event): void {
		const targetElement = event.target as HTMLElement;
		const notificationListViewHeight = targetElement.offsetHeight;
		const notificationListScrollHeight = targetElement.scrollHeight;
		const scrollLocation = targetElement.scrollTop;
		const buffer = 100;
		const limit = notificationListScrollHeight - notificationListViewHeight - buffer;
		if (scrollLocation > limit && this.nextPageToken && !this.isLoading() && this.totalNotificationsCount < 500) {
			this.isLoading.set(true);
			this.loadUnreadNotifications(this.nextPageToken);
		}
	}

	/**
	 * Fetches notifications from the server and updates the notification store.
	 */
	public loadUnreadNotifications(nextPageToken?: string): void {
		const params = {
			application: this.application,
			next: nextPageToken,
			filter: NotificationFilterEnum.UNREAD
		};

		this.notificationService
			.getNotifications$Response(params)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (notificationResponse) => {
					if (notificationResponse?.body?.items) {
						this.nextPageToken = notificationResponse?.body?.next;
						const unreadNotifications = notificationResponse.body.items;
						this.notificationsStore.prependOrPatchNotifications(
							unreadNotifications as unknown as NotificationStoreData[]
						);
						this.totalNotificationsCount = this.totalNotificationsCount + unreadNotifications.length;
						this.unReadNotificationsCalled.set(true);
						this.isLoading.set(false);
					}
				},
				error: () => {
					this.isApiCallFailed = true;
				}
			});
	}

	public clickedOutside(event: boolean): void {
		this.isFirstClick.set(!!event);
		if (event) {
			this.closeNotificationPopup.emit();
		}
	}

	/**
	 * Closes all dialogs and navigates to the notifications details page.
	 */
	public notificationDetails(): void {
		this.dialogService.closeAll();
		this.router.navigateByUrl('notifications-details');
	}

	/**
	 * Returns the current date in UTC format as a string.
	 */
	public getCurrentDateInUTC(): string {
		const currentDate = new Date();
		const currentdateFormat = new Date(currentDate.getTime() + 10000);
		return currentdateFormat.toISOString().slice(0, 19) + 'Z';
	}

	/**
	 * Opens a dialog with details of the selected notification.
	 * @param notification The notification to display details for
	 */
	public openPopup(notification: NotificationStoreData): void {
		this.dialogService.closeAll();
		const selectedNotification = this.notificationsData.find((item) => item.id === notification.id);
		// Define the notificationId based on the type of notification passed

		this.dialogService.openDialog(NotificationDetailsMainComponent, {
			data: { notificationData: selectedNotification, isListPage: false }
		});
	}

	/**
	 * Get all unread notifications.
	 */
	public getAllUnreadNotifications(): Observable<NotificationStoreData[]> {
		return this.notificationService
			.getNotifications$Response({
				filter: NotificationFilterEnum.UNREAD
			})
			.pipe(
				expand((response) => {
					if (!response.body?.next) {
						return EMPTY;
					}

					return this.notificationService.getNotifications$Response({
						filter: NotificationFilterEnum.UNREAD,
						next: response.body.next
					});
				}),
				map((response) => response.body?.items ?? []),
				reduce((all, items) => [...all, ...(items as NotificationStoreData[])], [] as NotificationStoreData[])
			);
	}

	/**
	 * Marks all unread notifications as read.
	 */
	public clearAll(): void {
		this.getAllUnreadNotifications()
			.pipe(
				map((notifications) => notifications.map((notification) => notification.id)),
				map((ids) => {
					const chunks: string[][] = [];

					for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
						chunks.push(ids.slice(i, i + CHUNK_SIZE));
					}

					return chunks;
				}),
				concatMap((chunks) =>
					from(chunks).pipe(
						concatMap((chunk) => {
							const requestData = {
								body: chunk.map((id) => ({
									id,
									isRead: true
								}))
							};

							const readNotifications = chunk.map((id) => ({
								id,
								readAt: this.getCurrentDateInUTC()
							}));
							return this.notificationService.postNotifications(requestData).pipe(
								tap(() => {
									this.notificationsStore.prependOrPatchNotifications(readNotifications);
								})
							);
						})
					)
				),
				catchError(() => {
					this.snackBarService.open({
						message: this.translocoService.translate('cloud.shared.notification.unable-to-mark-as-read'),
						actionText: this.translocoService.translate('common.snackbar.dismiss')
					});
					return EMPTY;
				}),
				takeUntil(this.destroy$)
			)
			.subscribe();
	}
	public markAsRead(notificationData: NotificationStoreData): void {
		this.successApiCallCount = 0;
		const requestData = {
			body: [
				{
					id: notificationData.id,
					isRead: true
				}
			]
		};
		const readNotification = [
			{
				id: notificationData.id,
				readAt: this.getCurrentDateInUTC()
			}
		];
		this.updateNotifications(requestData, readNotification);
	}

	/**
	 * Post API call for updating notifications status.
	 */
	public updateNotifications(
		requestData: { body: PostNotificationsRequest },
		updatedNotifications: Array<Pick<NotificationStoreData, 'id'> & Partial<NotificationStoreData>>
	): void {
		this.notificationService
			.postNotifications(requestData)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: () => {
					this.successApiCallCount += 1;
					this.notificationsStore.prependOrPatchNotifications(updatedNotifications);
				},
				error: () => {
					if (this.successApiCallCount === 0) {
						this.snackBarService.open({
							message: this.translocoService.translate(
								'cloud.shared.notification.unable-to-mark-as-read'
							),
							actionText: this.translocoService.translate('common.snackbar.dismiss')
						});
					} else {
						this.snackBarService.open({
							message: this.translocoService.translate(
								'cloud.shared.notification.unable-to-mark-some-notifications-as-read'
							),
							actionText: this.translocoService.translate('common.snackbar.dismiss')
						});
					}
				}
			});
	}

	/**
	 * Closes all open dialogs.
	 */
	public closedialog(): void {
		this.closeNotificationPopup.emit();
		this.dialogService.closeAll();
	}

	/**
	 * Closes all dialogs and navigates to the notifications list page.
	 */
	public notificationsList(): void {
		this.closeNotificationPopup.emit();
		this.dialogService.closeAll();
		this.navigationService.toShureCloudUrl('/notifications-list');
	}

	/**
	 * Performs cleanup when the component is destroyed.
	 */
	public ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	/**
	 * Builds the popup header date using user date preferences.
	 */
	private formatCurrentDate(): string {
		return getConvertedDate(this.timeZone, this.displayDateFormat, this.currentDate.toISOString());
	}
}
